import { seoRouteGroups } from "@/lib/seoRouteIndex";

export const publicSeoHubs = {
  routes: {
    eyebrow: "Route Library",
    title: "Discover Smarter Sailing Routes Worldwide",
    description: "Explore public route intelligence for sailors and skippers, including distance planning, risk factors, recommended stops, marina context, and passage preparation.",
    cta: "Explore Routes",
    heroImage: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1400&q=80",
    schemaType: "CollectionPage",
    sections: [
      {
        title: "Popular Route Types",
        items: [
          "Coastal cruising routes",
          "Island-hopping passages",
          "Marina-to-marina sailing plans",
          "Weather-aware offshore routes",
          "Fuel-efficient motor yacht passages",
          "Beginner-friendly day sails"
        ]
      },
      {
        title: "Route Intelligence",
        items: [
          "Distance and estimated passage time",
          "Weather exposure and seasonal risks",
          "Recommended marina stops",
          "Fuel planning considerations",
          "Navigation difficulty scoring",
          "Safety and emergency planning notes"
        ]
      }
    ],
    faqs: [
      { question: "Are route pages free to access?", answer: "Yes. Public route discovery remains free and indexable. Premium tools add AI optimization, weather routing, and advanced operational planning." },
      { question: "Can I use routes for real navigation?", answer: "Routes are educational planning aids. Always verify charts, weather, local rules, and official nautical publications before departure." }
    ],
    featuredPages: seoRouteGroups.routes,
    related: ["/learning-center", "/marinas", "/weather", "/skipper-tools"]
  },
  ports: {
    eyebrow: "Port Discovery",
    title: "Find Ports, Entry Notes, and Maritime Services",
    description: "A public port discovery hub for entry preparation, facilities, local services, nearby marinas, and skipper planning resources.",
    cta: "Explore Ports",
    heroImage: "https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=1400&q=80",
    schemaType: "CollectionPage",
    sections: [
      {
        title: "Port Information",
        items: [
          "Entry preparation notes",
          "Nearby marina services",
          "Fuel and provisioning context",
          "Weather exposure considerations",
          "Local maritime contacts",
          "Recommended arrival checks"
        ]
      },
      {
        title: "For Skippers",
        items: [
          "Arrival planning",
          "Documentation reminders",
          "Port service discovery",
          "Safety and shelter guidance",
          "Local regulations context",
          "Trip preparation resources"
        ]
      }
    ],
    faqs: [
      { question: "Is port information public?", answer: "Yes. Port discovery and educational preparation content is designed to be public and search-friendly." },
      { question: "Can ports be connected to premium tools?", answer: "Yes. Premium workflows can combine port context with route planning, weather analysis, and AI skipper assistance." }
    ],
    featuredPages: seoRouteGroups.ports,
    related: ["/port-entry", "/marinas", "/routes", "/skipper-tools"]
  },
  regulations: {
    eyebrow: "Regulations Center",
    title: "Understand Sailing Rules, Entry Requirements, and Maritime Regulations",
    description: "A public maritime regulations hub built for skippers preparing international passages, charter trips, and coastal cruising.",
    cta: "Explore Regulations",
    heroImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1400&q=80",
    schemaType: "CollectionPage",
    sections: [
      {
        title: "Regulation Topics",
        items: [
          "Country entry requirements",
          "Transit log preparation",
          "Customs and immigration notes",
          "VHF and safety obligations",
          "COLREGs and collision-prevention rules",
          "Navigation lights and sound signals",
          "Safety equipment requirements",
          "Protected-area anchoring restrictions",
          "Waste discharge and pollution rules",
          "Charter documentation basics"
        ]
      },
      {
        title: "Planning Benefits",
        items: [
          "Reduce arrival uncertainty",
          "Prepare documents earlier",
          "Understand country-specific differences",
          "Avoid avoidable delays",
          "Support safer route decisions",
          "Connect regulations with port planning"
        ]
      }
    ],
    faqs: [
      { question: "Does Marstick replace official regulations?", answer: "No. Marstick helps organize maritime knowledge, but skippers must verify requirements with official authorities before sailing." },
      { question: "Will regulations pages support multiple countries?", answer: "Yes. The architecture is designed for scalable country, port, and route-specific regulation pages." },
      { question: "What documents should I check before entering a new country by boat?", answer: "Skippers should usually prepare passports, vessel registration, insurance, crew lists, radio licenses, transit logs, and any country-specific entry forms before arrival." },
      { question: "Do maritime regulations change often?", answer: "Yes. Entry rules, anchoring limits, customs procedures, and safety requirements can change, so always confirm the latest information before departure." },
      { question: "Are anchoring rules the same in every country?", answer: "No. Anchoring restrictions vary by country, region, protected area, season, and local authority, especially near marine parks, harbors, and busy coastal zones." },
      { question: "Why are customs and immigration procedures important for sailors?", answer: "Correct customs and immigration procedures help avoid fines, delays, denied entry, or problems when leaving the country or visiting the next port." },
      { question: "Can regulations affect route planning?", answer: "Yes. Clearance ports, restricted zones, mandatory reporting areas, safety equipment rules, and seasonal restrictions can all influence the safest and most practical route." }
    ],
    featuredPages: seoRouteGroups.regulations,
    related: ["/marinas", "/learning-center", "/safety", "/contact"]
  },
  glossary: {
    eyebrow: "Nautical Glossary",
    title: "Learn Maritime Terms, Seamanship Language, and Navigation Concepts",
    description: "A public nautical glossary foundation for sailors, students, skippers, and maritime professionals learning the language of the sea.",
    cta: "Explore Glossary",
    heroImage: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1400&q=80",
    schemaType: "DefinedTermSet",
    sections: [
      {
        title: "Glossary Categories",
        items: [
          "Navigation terms",
          "Sailing maneuvers",
          "Weather terminology",
          "Safety and emergency language",
          "Marina and docking terms",
          "Regulation and charter vocabulary"
        ]
      },
      {
        title: "Learning Value",
        items: [
          "Clear beginner-friendly definitions",
          "Internal links to lessons",
          "Practical onboard examples",
          "Multilingual expansion ready",
          "Useful for maritime SEO",
          "Supports structured learning paths"
        ]
      }
    ],
    faqs: [
      { question: "Who is the glossary for?", answer: "It is for new sailors, skipper students, charter guests, and professionals who want quick maritime definitions." },
      { question: "Will glossary pages link to lessons?", answer: "Yes. Glossary terms are designed to connect naturally with learning articles, regulations, and route planning content." },
      { question: "How should beginners use the nautical glossary?", answer: "Start with common navigation, docking, weather, and safety terms, then follow related learning pages to understand how each term is used onboard." },
      { question: "Are glossary definitions suitable for real voyages?", answer: "They are educational references. Always confirm operational decisions with official charts, local rules, instructor guidance, and current maritime publications." },
      { question: "Does the glossary include emergency and safety terms?", answer: "Yes. Safety language such as distress calls, VHF terminology, emergency signals, and basic response concepts is part of the glossary structure." },
      { question: "Can I use the glossary before a sailing course?", answer: "Yes. It is designed to help students learn key maritime vocabulary before lessons, practical training, charter briefings, or skipper exams." },
      { question: "Will more glossary terms be added over time?", answer: "Yes. The glossary is built to expand with more definitions, examples, related lessons, and multilingual maritime vocabulary." }
    ],
    featuredPages: seoRouteGroups.glossary,
    related: ["/learning-center", "/training-center", "/regulations", "/safety"]
  }
};